<script setup>
import { ref, computed, watch, onMounted, createApp } from "vue"
import { $bus, mqtt } from '@/common/events'
import { defaultServiceOptions } from '@/common/config'
import { dialog } from '@/common/dialog'
import { toast } from '@/common/toast'
import { $virtualKeyboard } from '@/common/helper'
import { useTitle } from '@/composables/title'
import { useTranslate } from '@/composables/language'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useSettings } from '@/composables/settings'
import { useAuthState } from '@/composables/auth'
import { useMasterCabinet } from "@/composables/cabinet.js";
import { useCabinetStatus } from "@/composables/cabinet.js";
import { useSessionTimer } from "@/composables/session";
import { InventoryService } from '@/services/InventoryService.js'
import { ArticleService } from '@/services/ArticleService.js'
import { CabinetService } from '@/services/CabinetService.js'
import { ConfigService } from '@/services/ConfigService.js'
import { UserService } from '@/services/UserService.js'
import { LogService } from '@/services/LogService.js'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import Pagination from '@/components/Pagination.vue'
import DropDown from '@/components/DropDown.vue'
import Drawer from '@/components/Drawer.vue'
import Toast from '@/components/Toast.vue'
import Label from '@/components/Label.vue'
import TextBox from '@/components/TextBox.vue'
import TextArea from '@/components/TextArea.vue'
import CheckBox from '@/components/CheckBox.vue'
import Button from '@/components/Button.vue'
import Fullscreen from '@/components/Fullscreen.vue'
import AutocompleteTextBox from '@/components/AutocompleteTextBox.vue'

const { t } = useTranslate();

const isModalVisible = ref(false);
const isOpening = ref(false);
const isOpen = ref(false);
const isLoading = ref(true);
const user = ref(null);

let articleService = null;
let cabinetService = null;
let configService = null;
let userService = null;
let logService = null;
let inventoryService = new InventoryService();

const authStore = useAuthState();

const cabinetId = ref(null);
const cabinetName = ref(null);
const settings = ref({ });
const trays = ref(null);
let originalItems = null;

//paging = pagination or scroll lazy-load
const searchText = ref("");

const categoryBreadcrumbs = ref([
  { id: "_root", name: t('gloves.all_articles') },
  //{ id: "CT123", name: "Kovinski" },
  //{ id: "CT234", name: "Vijaki" },
]);

const isFilterVisible = ref(false);
/*const isDrawerVisible = ref({
  get() {
    return isFilterVisible.value;
  },
  set(value) {
    isFilterVisible.value = value;
  }
});*/

/*
data.config.gloves.limitCount = data.glovesLimitCount;
data.config.gloves.limitInterval = data.glovesLimitInterval;
data.config.gloves.maxUsageTime = data.glovesMaxTime;
*/

const someoneForgotDoorOpen = ref(false);
const hasUserInteraction = ref(false);

const isCabinetBorrowMode = computed(() => true);

const isSearchFilter = computed(() => {
  return searchText.value?.length > 0;
});

//Must be reactive variable to trigger "computed"
const allPossibleCategories = ref([ ]);
const allPossibleParameters = ref([ ]);
const allPossibleArticles = ref([ ]);

//Current category list
const parentCategoryId = ref("_root"); //null for root
const selectedCategoryId = ref("_root"); //null for root

const isRootCategory = computed(() => selectedCategoryId.value == null || selectedCategoryId.value == "_root");
const isLastCategory = computed(() => !categories.value || categories.value.length == 0); //No subcategories

const isShowAllArticles = ref(false);
const isUserPrefSize = ref(false);

const selectedSortOption = ref("default");

const trayList = ref([
  { name: "A01", value: "A01" },
  { name: "A02", value: "A02" },
  { name: "A03", value: "A03" },
]);

const unitList = ref([
  { name: "kos", value: "pcs" },
  { name: "m2", value: "m2" },
  { name: "g", value: "g" },
  { name: "kg", value: "kg" },
]);

const traySelect = ref("A01");
const unitSelect = ref("pcs");
const quantity = ref("");

const isEditStockQuantity = ref(false);
const virtualStockQuantity = ref(null);

const alphabet = ref("ABCDEFGHIJKLMNOPQRSTUVWXYZ".replace(/[QXYW]/gi, "").split(""));

const isTraysLoading = ref(true);
const loadedCabinetId = ref(null);
let waitForCabinetId = false;

/*
$bus.on("config", async (config) => {
  if (typeof config == "object" && waitForCabinetId) {
    await firstInit(config.cabinet.id);
  }
});
*/

const canSelectOtherSize = ref(false);

const hasTrays = computed(() => {
  return trays.value && trays.value.length > 0;
});

async function firstInit(id) {
  try {
    cabinetId.value = id;

    await loadSettings();
    await loadUser(authStore.userId.value);
    await loadCabinet();
    await loadGloveSizes();
    await loadGloveTrays();
    await refreshBorrowedTrays();
    //await loadGloves();

    isUserPrefSize.value = true;
    isLoading.value = false;

    let permissions = authStore.permissions.value;

    //Allow to choose other glove size if no user's size is available at the moment
    canSelectOtherSize.value = availableGlovesCount.value == 0;
    //canSelectOtherSize.value = permissions.includes("useCase.gloves.allowOtherSize");

    //Allow to choose user
    allowSelectUser.value = permissions.includes("useCase.gloves.allowExtra");
  }
  catch (err) {
    console.error(err);
  }

  isTraysLoading.value = false;

  //Can be loaded with delay
  loadUsers().then(x => { }).catch(console.error);
}

/*
dialog({
  type: "danger",
  title: "Zahtevek za potrditev",
  text: "Artikel bo izbrisan iz kataloga. Si prepričan?",
  confirmButtonText: "Zbriši",
  cancelButtonText: "Prekliči",
});
*/

/*
dialog({
  type: "success",
  //title: "Potrditev",
  text: "Storitev uspešno opravljena",
  confirmButtonText: "V redu",
  //cancelButtonText: "Prekliči",
});
*/

onMounted(async () => {
  useTitle(t('menu.gloves'));
  useMenu("gloves");
  useSubmenu(false);
  useTopBar(true);

  // setTimeout(() => {
  //   toast({ type: "success", text: "Primer sporočila" });
  // }, 10);
    
  isLoading.value = true;

  await loadSettings();

  //Cache cabinet data to local storage
  // const cabinetStore = useCabinetStore();
  // let piniaCabinet = cabinetStore.getCabinet();
  const { masterCabinet } = useMasterCabinet();
  let piniaCabinet = masterCabinet.value;
  if (piniaCabinet) {
    cabinetId.value = piniaCabinet.id;
  }
  
  articleService = new ArticleService(defaultServiceOptions());
  cabinetService = new CabinetService(defaultServiceOptions());
  inventoryService = new InventoryService(defaultServiceOptions());
  //configService = new ConfigService(defaultServiceOptions()); //Local or remote
  configService = new ConfigService(); //Local only
  userService = new UserService(defaultServiceOptions());
  logService = new LogService(defaultServiceOptions());

  let id = piniaCabinet?.id;
  if (id) {
    await firstInit(id);
  }
  else {
    //waitForCabinetId = true;
  }

  try {
    mqtt.ready(initMessageQueue);
  }
  catch (err) {
    dialog(err);
  }
});

let previousWasOpen = false;

let openingTimer = null;
const isPrevOpen = ref(false);

async function initMessageQueue() {
  mqtt.on('received', (data) => {
    //console.log("[mqtt] received", data);

    if (data.topic == "hcu/cabinet/drum/debug") {
      //console.log("debug", data.payload);
      if (data.payload.text == "OK") {
        isOpening.value = true;
        isOpen.value = false;
        isPrevOpen.value = false;

        if (openingTimer) {
          clearTimeout(openingTimer);
          openingTimer = null;
        }
      }
      /*else if (data.payload.text == "READY") {
        isOpening.value = false;
        isPrevOpen.value = false;

        isOpen.value = true; //Open

        if (openingTimer) {
          clearTimeout(openingTimer);
          openingTimer = null;
        }
      }*/
      else if (data.payload.text == "DONE") {
        isOpening.value = false;
        isPrevOpen.value = false;

        if (openingTimer) {
          clearTimeout(openingTimer);
          openingTimer = null;
        }
      }
      else if (data.payload.text == "ERR") {
        isOpening.value = false;

        if (openingTimer) {
          clearTimeout(openingTimer);
          openingTimer = null;
        }
      }
      else if (data.payload.text == "Close and continue") {
        isOpen.value = true;
        isOpening.value = false;
        isPrevOpen.value = true;

        if (openingTimer) {
          clearTimeout(openingTimer);
          openingTimer = null;
        }
      }
      else if (data.payload.text && /^[01]{10,50}$/g.test(data.payload.text)) {
        let anyDoorOpen = (data.payload.text.indexOf("1") >= 0);
        if (anyDoorOpen && isOpen.value != anyDoorOpen) {
          //Only on change
          //isEditStockQuantity.value = true;
        }
        isOpen.value = anyDoorOpen; //triggered many time

        //Check if anyone left door open from before
        if (anyDoorOpen && !hasUserInteraction.value) {
          someoneForgotDoorOpen.value = true;
          hasUserInteraction.value = true;
        }
        if (!anyDoorOpen && someoneForgotDoorOpen.value) {
          someoneForgotDoorOpen.value = false;
        }

        //Cannot be rotating while door is open, therefore assume it is not rotating (isOpening = false)
        if (anyDoorOpen) {
          previousWasOpen = true;
          isOpening.value = false;

          //DOOR OPEN
          //isOpen.value = true;

          //External events can interrupt user activity, e.g. smartcard removed from reader will cause log out
          $bus.emit("interrupt promise", { canInterrupt: true }); //TODO: Do NOT allow interrupttions until doors are closed

          //Resume session timeout timer
          useSessionTimer(true); //TODO: Do NOT allow interrupttions until doors are closed
        }
        else if (previousWasOpen) {
          previousWasOpen = false;

          //Door has just been closed
          //DOOR CLOSED (triggered once)
          //isOpen.value = false;

          //Pending action can be invoked once
          if (pendingAction.value) {
            pendingAction.value = false;
            let func = pendingFunc;
            pendingFunc = null;
            func().then(x => { }).catch(err => console.error("pendingFunc: func()", err));
          }
        }

      }
    }
    
    //TODO: When open
    //$watchdog.start();

    if (data.topic == "hcu/cabinet/drum/position") {
      //console.log("position", data.payload);
      if (data.payload && typeof data.payload.inputState != "undefined") {
        let before = isOpen.value;
        isOpen.value = (data.payload.inputState > 0);
        let after = isOpen.value;
        if (after == true && before != after) { //Only if changed to true
          isEditStockQuantity.value = true;
          if (openingTimer) {
            clearTimeout(openingTimer);
            openingTimer = null;
          }
        }
      }
    }

    if (data.topic == "hcu/cabinet/drum/reply") {
      //console.log("drum/reply", data.payload.message);
    }


    if (data.topic == "hcu/cabinet/drum/open/response") {
      //console.log("open/response", data.payload);
    }


  });
}

async function loadGloves() {
  rows.value = [
    {
      name: "Rokavica L",
      size: "L",
      stock: {
        new: [ "A1", "A2", "A3" ],
        using: [ ],
        used: [ "B1", "B2" ],
      }
    },
    {
      name: "Rokavica M",
      size: "M"
    },
    {
      name: "Rokavica S",
      size: "S"
    }
  ];

  //TODO: Filter by user privileges
}

//const alphabet = ref("ABCDEFGHIJKLMNOPQRSTUVWXYZ".replace(/[QXYW]/gi, "").split(""));

//const settings = ref(null);
const gloveSizeOptions = ref(null);
const gloveStats = ref(null);

//A9-C15 => { x1: 1, y1: 9, x2: 3, y2: 15 }
function parseRange(range) {
  let m = /^([A-Z])([0-9]+)\-([A-Z])([0-9]+)$/.exec(range);
  if (!m) {
    return;
  }

  let x1 = alphabet.value.indexOf(m[1]) + 1;
  let y1 = parseInt(m[2]);

  let x2 = alphabet.value.indexOf(m[3]) + 1;
  let y2 = parseInt(m[4]);

  return { x1, y1, x2, y2 };
}

async function loadCabinet() {
  let cabinet = await cabinetService.getCabinet(cabinetId.value);
  console.log("[loadCabinet]", cabinet);
  if (cabinet) {
    cabinetName.value = cabinet.name;
  }
}

async function loadGloveSizes() {
  let data = await cabinetService.getRemoteSettings();
  if (data.gloveSizeOptions && data.gloveSizeOptions.length) {
    let sizes = data.gloveSizeOptions.filter(x => /^[A-Z][0-9]+\-[A-Z][0-9]+$/.test(x.range)).map(x => {
      return {
        value: x.value,
        range: x.range,
        ...parseRange(x.range)
      };
    });
    console.log("gloveSizeOptions", sizes);
    gloveSizeOptions.value = sizes;
  }
  settings.value = data;
}

function getGloveSize(trayName, x, y) {
  let size = gloveSizeOptions.value.find(g => (g.x1 <= x && x <= g.x2) && (g.y1 <= y && y <= g.y2));
  if (size) {
    return size.value;
  }
  return null;
}

async function loadGloveTrays() {
  //cabinetId.value = route.params.id;

  let positions = [ { name: "Vse", value: "_all" } ];

  let list = [ ];
  for (let j = 1; j <= 23; j++) {
    positions.push({ name: j.toString(), value: j.toString() });
    for (let i = 1; i <= 18; i++) {
      let trayName = alphabet.value[i - 1] + j.toString();
      list.push({
        id: cabinetId.value.replace(/^CA/g, "CY") + trayName,
        drumPosition: j,
        doorNumber: i,
        trayIndex: i,
        trayName: trayName,
        articleId: null,
        sku: null,
        name: null, //Article name
        quantity: null,
        unit: "pcs",
        gloveSize: getGloveSize(trayName, i, j)
      });
      //{ index: 1, sku: "123", name: "Zaščitne rokavice", quantity: 1, unit: "pcs" },
    }
  }

  let result = await cabinetService.getCabinetTrays(cabinetId.value);
  if (result && result.items && result.count > 0) {
    for (let item of list) {
      let record = result.items.find(x => x.id == item.id);
      if (record) {
        if (record.gloveSize) {
          item.gloveSize = record.gloveSize; //Override from database
        }
        item.usage = record.usage;
        item.userId = record.userId;
      }
    }
  }

  trays.value = list;
  originalItems = result.items;

  console.log("[loadGloveTrays]", list);
  console.log("total", list.length);
  console.log("free", list.filter(x => !x.usage || x.usage == "empty").length);
  console.log("borrowed", list.filter(x => x.usage == "borrowed").length);
  console.log("borrowed by user", list.filter(x => x.usage == "borrowed" && x.userId == user.value?.id).length);
  console.log("dirty", list.filter(x => x.usage == "used").length);
  console.log("[gloves]", trays.value.length, hasTrays.value);
}

//Search by text - optimized, to avoid refiltering by category and parameters
//const rowsFiltered = computed(() => rows.value?.find(x => x.size == userGloveSize.value));
const rowsFiltered = computed(() => rows.value?.find(x => x.size == selectedGloveSize.value));

async function applyStock(item) {
  let trayName = selectedTray.value || item.tray; //TODO: item.tray is deprecated, use selectedTray.value

  try {
    let data = { trays: [
      {
        findTrayId: true,
        //id: item.id,
        articleId: item.id,
        quantity: virtualStockQuantity.value,
        unit: "pcs",
        name: trayName, //Tray name
        /*data: {
          doorNumber: item.doorNumber,
          drumPosition: item.drumPosition,
        }*/
      }
    ] };

    /*
    for (let tray of data.trays) {
      //TODO: Use unitList to find the real value
      if (tray.unit == "kos") { //Quick work-around
        tray.unit = "pcs";
      }
    }
    */

    if (isCabinetBorrowMode.value) {
      data = { trays: [
        {
          findTrayId: true,
          articleId: item.id,
          quantity: 1,
          unit: "pcs",
          name: item.tray, //Tray name, e.g. A1
          usage: selectedUsage.value,
          userId: user.value?.id
        }
      ] };
    }

    let result = await cabinetService.updateCabinetTrayStock(cabinetId.value, data);
  }
  catch (err) {
    dialog(err);
    return;
  }

  try {
    if (isCabinetBorrowMode.value) {
      let translated = null;
      switch (selectedUsage.value) {
        case "new": translated = "novo"; break;
        case "used": translated = "uporabljeno"; break;
        case "empty": translated = "prazen predal"; break;
        case "borrowed": translated = "izposojeno"; break;
      }
      let result = await logService.insertLog({
        uid: "user", //TODO: User id and name
        tid: item.tray,
        msg: "Sprememba stanja na '" + translated +  "' za " + item.name,
        ts: new Date()
      });
    }
    else {
      let result = await logService.insertLog({
        uid: "user", //TODO: User id and name
        tid: item.tray,
        msg: "Sprememba količine " + item.stock +  " -> " + virtualStockQuantity.value + " za " + item.name,
        ts: new Date()
      });
    }
  }
  catch (err) {
    console.error(err);
  }
  
  item.stock = virtualStockQuantity.value; //TODO: Deprecated
  selected.value.stock = virtualStockQuantity.value; //TODO: Deprecated

  let trayItem = selected.value?.trays?.find(x => x.name == trayName);
  if (trayItem) {
    trayItem.quantity = virtualStockQuantity.value;
  }

  isEditStockQuantity.value = false;

  //Live update the icon on tray button
  if (isCabinetBorrowMode.value && selectedTrayItem.value) {
    selectedTrayItem.value.usage = selectedUsage.value;
  }

  //Close modal window if cabinet door is already closed
  if (isModalVisible.value && !isOpen.value) {
    closeModal(); //Automatically close modal window when door is closed
  }
}

//Select best tray algorithm
function findBestTray(trays) {
  //First algorithm mode
  //TODO: Select by nearest rotation

  //Second algorithm mode
  let selected = trays[0];
  for (let tray of trays) {
    if (tray.quantity > 0) {
      //Better if tray has at least one item than empty
      if (!selected.quantity) {
        selected = tray;
        continue;
      }
      //Least quantity goes first to empty the tray as soon as possible
      if (tray.quantity < selected.quantity) {
        selected = tray;
        continue;
      }
      //TODO: tray.quantity > selected.quantity if this is the user's preference
    }
  }
  return selected;
}

function showModal(row) {
  selected.value = row;
  selectedTray.value = null;
  availableTraySelect.value = null;

  //In case if only a single tray
  if (row && row.trays && row.trays.length == 1) {
    selectedTray.value = row.trays[0].name;
    availableTraySelect.value = row.trays[0].name; //TODO: tray.id to select also which cabinet
  }
  else if (row && row.trays && row.trays.length > 1) {
    availableTraySelect.value = selectedTray.value = findBestTray(row.trays).name;
  }

  isModalVisible.value = true;
  isRelatedArticlesView.value = false;
  //virtualStockQuantity.value = row.stock; //TODO: Deprecated
  virtualStockQuantity.value = null; //Cannot tell the quantity until the tray is selected
  isEditStockQuantity.value = false;
  //isOpening.value = false;
  isOpen.value = false;

  $virtualKeyboard.hide();
}

function closeModal() {
  isModalVisible.value = false;
}

function closeModalConditional(insideModalWindowElement) {
  //Check if clicked inside the modal window
  if (insideModalWindowElement) {
    return; //Avoid handling
  }

  //Avoid closing modal window when user action needs to be perfomed
  if (isOpen.value || isOpening.value) {
    return false;
  }

  //Close modal window
  closeModal();
}

async function openTray(tray) { //TODO: Rename to trayName
  hasUserInteraction.value = true;

  //Any door already open?
  if (isOpen.value) {
    return;
  }

  //Opening already in progress?
  if (isOpening.value) {
    return;
  }

  isOpening.value = true;
  isOpen.value = false;

  try {
    $bus.emit("interrupt promise", { canInterrupt: false });

    //Avoid session timeout while a tray is open
    useSessionTimer(false);

    if (openingTimer) {
      clearTimeout(openingTimer);
      openingTimer = null;
    }

    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);

    //B01
    let m = /^([A-Z])(\d+)$/.exec(tray);
    if (!m) {
      throw new Error("Regex: " + tray);
    }

    let drumPosition = parseInt(m[2]);
    let doorNumber = alphabet.value.indexOf(m[1]) + 1;
    //console.log(`${tray} => ${doorNumber} ${drumPosition}`);

    /*
    let itemTray = selected.value?.trays?.find(x => x.name == tray);
    if (itemTray) {
      virtualStockQuantity.value = itemTray.quantity;
    }
    */

    try {
      let result = await logService.insertLog({
        uid: user.value?.displayName || user.value?.username,
        tid: tray,
        msg: "Odpiranje predala " + tray,
        ts: new Date()
      });
    }
    catch (err) {
      console.error(err);
    }

    //socket.emit("publish", { topic: "hcu/cabinet/drum/open", payload:{ drumPosition, doorNumber } });
    mqtt.publish({ topic: "hcu/cabinet/drum/open", payload: { drumPosition, doorNumber } });

    openingTimer = setTimeout(function() {
      dialog(new Error(t('gloves.open_door_not_signaled')));
      isOpening.value = false;
      isOpen.value = false;
    }, 25000);

    //$watchdog.start();
  }
  catch (err) {
    $bus.emit("interrupt promise", { canInterrupt: true });
    useSessionTimer(true);
    isOpening.value = false;
    //logRecords.value.push({ text: err.message, type: "danger", direction: "outbound" });
    dialog(err);
  }

  /*
  setTimeout(() => {
    isEditStockQuantity.value = true;
    isOpening.value = false;
    isOpen.value = true;
  }, 3000);
  */
}

function stopTray(i) {
  isOpening.value = false;

  if (openingTimer) {
    clearTimeout(openingTimer);
    openingTimer = null;
  }
}

const rows = ref([ ]);

const rowsLoaded = ref([ ]);

//const rowsFiltered = computed(() => rows.value);

function updateQuantity(delta) {
  let qty = virtualStockQuantity.value;
  let isPositiveNumber = (qty === 0 || qty > 0);
  if (!isPositiveNumber) {
    qty = 0;
  }
  if (!delta) {
    delta = 0;
  }
  qty += delta;
  if (qty < 0) {
    qty = 0;
  }
  virtualStockQuantity.value = qty;
}

async function loadSettings() {
  // autoConfirm.value = config?.usage?.fastFilling === true;

  //Note: work-around to read value immediately from the database because useSettings() load delay
  let config = await configService.getSettings();

  //const { settings } = useSettings();
  const result = useSettings();
  settings.value = result.settings?.value;
  //scale.value = parseInt(settings.value.display?.scale) || 100;
  scale.value = parseInt(config.display?.scale || settings.value.display?.scale) || 100;
  console.log("[scale]", scale.value, config.display?.scale, settings.value.display?.scale);
}

async function loadUser(userId) {
  if (userId) {
    //Get user info from database
    let record = await userService.getUser(userId, { gloves: true });
    console.log("user", record);
    user.value = record;

    selectedUser.value = userId;
    selectedUserName.value = record.displayName || record.username || record.email || "Uporabnik";

    //User's glove size
    userGloveSize.value = record.preferences?.gloveSize; //S, M, L
    selectedGloveSize.value = userGloveSize.value;

    //Check if already borrowed from another cabinet (neighbour cabinet)
    glovesBorrowed.value = user.glovesBorrowed;
    hasBorrowedGlovesOtherCabinet.value = false;
    if (record.glovesBorrowed && record.glovesBorrowed.length > 0) {
      //Borrowed from other cabinet
      let otherCabinet = record.glovesBorrowed.filter(x => x.cabinetId != cabinetId.value);
      hasBorrowedGlovesOtherCabinet.value = (otherCabinet.length > 0);

      //Bug fix: to prevent paradox in case user has managed to take gloves on both cabinets at the same time (cloned smart card or kept login scenario)
      if (hasBorrowedGlovesOtherCabinet.value && record.glovesBorrowed.length > 1) {
        let thisCabinet = record.glovesBorrowed.filter(x => x.cabinetId == cabinetId.value);
        if (thisCabinet) {
          hasBorrowedGlovesOtherCabinet.value = false;
        }
      }
    }
  }
}

const availableUsers = ref(null);

async function loadUsers() {
  let result = await userService.getUsers({ fields: "id,displayName,username", skip: 0, take: 100, gloves: true, tag: "glove-user" });
  availableUsers.value = result.items.filter(x => x.status == "active");
}

const glovesBorrowed = ref(null);
const selectedTray = ref(null);
const selectedTrayItem = ref(null);
const selectedUsage = ref(null);

function selectTray(tray) {
  if (!tray && availableTraySelect.value) {
    tray = selected.value?.trays?.find(x => x.name == availableTraySelect.value);
  }

  selectedTrayItem.value = tray;
  selectedTray.value = tray ? tray.name : null;
  // if (selected.value && selected.value.trays) {
  //   for (let t of selected.value.trays) {
  //     t.selected = (t == tray);
  //   }
  // }
  selectedUsage.value = tray ? tray.usage : null;
}

function setArticleUsage(usage) {
  // if (selectedTrayItem.value) {
  //   selectedTrayItem.value.usage = usage;
  // }
  selectedUsage.value = usage;
}

const returnArticle = ref(null);
const returnArticleTrayName = ref(null);

const takeArticle = ref(null);
const takeArticleTrayName = ref(null);

const isReturnProcedure = ref(false);
const isTakingProcedure = ref(false);
const isChoosingAction = ref(false);

const rowsBorrowed = computed(() => {
  let userId = user.value?.id;
  return rows.value.filter(x => x.trays?.find(y => y.usage == "borrowed" && y.userId == userId));
  //return rows.value.filter(x => x.trays?.find(y => y.usage == "borrowed"));
});

const rowsAvailable = computed(() => {
  //return rows.value.filter(x => x.trays?.find(y => y.usage == "new"));
  return trays.value && trays.value.filter(x => x.usage == "new");
});

const singleArticle = computed(() => {
  return rows.value && rows.value.length > 0 ? rows.value[0] : null;
});

const itemBorrowed = computed(() => {
  let list = rowsBorrowed.value;
  if (!list || list.length == 0) {
    return null;
  }

  let userId = user.value?.id;

  let item = list[0];
  let trays = item.trays && item.trays.filter(y => y.usage == "borrowed" && y.userId == userId);
  //TODO: Algorithm to use longest waiting - first inserted - or first set to usage 'new'

  return {
    id: item.id,
    name: item.name,
    article: item,
    tray: trays[0]
  };
});

const quotaReached = ref(false);

const { anyDoorOpen } = useCabinetStatus();
watch(anyDoorOpen, (newValue) => {
  if (newValue) {
    console.log("EVENT DOOR OPEN");
  }
  else {
    console.log("EVENT DOOR CLOSED");
    warningDoorOpen.value = false;
  }
});

const warningDoorOpen = ref(anyDoorOpen.value);
if (warningDoorOpen.value) {
  console.log("INITIAL DOOR OPEN");
}

async function refreshBorrowedTrays() {
  borrowedTray.value = findLastBorrowedTray();
  //console.log("refresh", borrowedTray.value)

  try {
    let userId = selectedUser.value;
    console.log("refreshBorrowedTrays", "userId", userId)
    if (userId) {
      let result = await cabinetService.getGloveLimit(userId);
      console.log("refreshBorrowedTrays", "result", result)
      quotaReached.value = !!result.quotaReached;
      console.log("refreshBorrowedTrays", "quotaReached", quotaReached.value)
      /*
      {
        "userId": "USzKG...VU",
        "unlimited": false,
        "quotaReached": true,
        "availableCount": 0,
        "availableTime": 600,
        "extraGlovesCount": 0,
        "limitCount": 1,
        "limitInterval": 600,
        "maxUsageTime": 1800,
        "usageTime": 77196.169,
        "usageElapsed": true,
        "hasBorrowed": true
      }
      */
    }
  }
  catch (err) {
    console.error("refreshBorrowedTrays", err);
  }
}

function findLastBorrowedTray() {
  let trays = getBorrowedTrays();
  if (trays && trays.length > 0) {
    let last = trays[trays.length - 1];
    return last;
  }
  return null;
}

function findNextAvailableTray() {
  let newGloveTrays = getAvailableUserTrays();
  if (newGloveTrays && newGloveTrays.length > 0) {
    let first = newGloveTrays[0];
    return first;
  }
  return null;
}

const borrowedTray = ref(null);
const hasBorrowedGloves = computed(() => !!borrowedTray.value);
const hasBorrowedGlovesOtherCabinet = ref(null);

const itemAvailable = computed(() => {
  return null;

  /*
  //trays.value && trays.value.filter(x => x.usage == "new");
  let list = rowsAvailable.value;
  if (!list || list.length == 0) {
    return null;
  }

  let item = list[0];
  let trays = item.trays && item.trays.filter(x => x.usage == "new" && x.gloveSize == userGloveSize.value);
  //TODO: Algorithm to use longest waiting - first inserted - or first set to usage 'new'

  return {
    id: item.id,
    name: item.name,
    article: item,
    tray: trays[0]
  };
  */
});

function getAvailableTrays() {
  if (!hasTrays.value) {
    return null;
  }
  return trays.value.filter(x => x.usage == "new");
}

function getAvailableUserTrays() {
  // if (!userGloveSize.value) {
  //   return null;
  // }
  if (!selectedGloveSize.value) {
    return null;
  }
  let trays = getAvailableTrays();
  if (!trays) {
    return null;
  }
  //return trays.filter(x => x.gloveSize == userGloveSize.value);
  return trays.filter(x => x.gloveSize == selectedGloveSize.value);
}

function getBorrowedTrays() {
  if (!hasTrays.value) {
    return null;
  }
  return trays.value.filter(x => x.usage == "borrowed" && x.userId == user.value?.id);
}

const availableGlovesCount = computed(() => {
  //return rowsAvailable.value?.length;
  let trays = getAvailableUserTrays();
  return trays ? trays.length : 0;

  // if (!hasTrays.value) {
  //   return 0;
  // }

  // if (!userGloveSize.value) {
  //   return 0;
  // }

  // return trays.value.filter(x => x.usage == "new" && x.gloveSize == userGloveSize.value).length;

  /*
  let count = rowsFiltered.value?.stock?.new?.length;
  return count;

  //Count trays with "new" items
  let counter = rowsLoaded.value.map(x => x.trays ? x.trays.filter(y => y.usage == "new").length : 0);

  let sum = 0;
  for (let c of counter) {
    sum += c;
  }

  return sum;
  */
});

/*
function startReturnArticleProcedure() {
  returnArticle.value = itemBorrowed.value; //Return borrowed item
  returnArticleTrayName.value = returnArticle.value?.tray?.name;
  isReturnProcedure.value = true;
  isTakingProcedure.value = false;
  openTray(returnArticleTrayName.value);
}
*/

const temporaryGloveSize = ref(null);

function startReturnArticleProcedure() {
  hasUserInteraction.value = true;

  let tray = borrowedTray.value;
  if (!tray) {
    return;
  }

  returnArticle.value = tray;
  let trayName = tray.trayName; //Tray name
  if (!trayName) {
    return;
  }

  if (tray.gloveSize && selectedGloveSize.value != tray.gloveSize) {
    temporaryGloveSize.value = selectedGloveSize.value;
    selectedGloveSize.value = tray.gloveSize;
  }

  returnArticleTrayName.value = trayName;
  isReturnProcedure.value = true;
  isTakingProcedure.value = false;
  openTray(trayName);
}

function takeNextArticle() {
  console.log("takeNextArticle")
  hasUserInteraction.value = true;

  let tray = findNextAvailableTray();
  if (!tray) {
    return;
  }

  takeArticle.value = tray;
  let trayName = tray.trayName; //Tray name
  if (!trayName) {
    return;
  }

  takeArticleTrayName.value = trayName;
  isReturnProcedure.value = false;
  isTakingProcedure.value = true;
  openTray(trayName);
}

//Automatically confirm action to minify user interaction
const autoConfirm = ref(true);

async function cancelReturnArticle() {
  isReturnProcedure.value = isOpen.value;
  isChoosingAction.value = false;
  //returnArticle.value = null;
  //takeArticle.value = null;
}

async function confirmReturnArticle() {
  isReturnProcedure.value = isOpen.value;
  isChoosingAction.value = false;

  //returnArticle.value = null;
  //takeArticle.value = null;
  //TODO: Save to database

  /*
  console.log("itemBorrowed", itemBorrowed.value?.id);
  if (!itemBorrowed.value?.id) {
    return dialog(new Error("itemBorrowed not found!"))
  }

  let article = rows.value.find(x => x.id == itemBorrowed.value.id);
  if (!article) {
    return dialog(new Error("Article not found!"))
  }

  console.log("itemBorrowed tray name", returnArticleTrayName.value);

  console.log("article", article);

  console.log("article trays", article.trays);
  let tray = article.trays.find(x => x.name == returnArticleTrayName.value);
  if (!tray) {
    return dialog(new Error("Tray not found!"))
  }

  tray.userId = user.value?.id;
  tray.usage = "used";
  //itemBorrowed.value == null

  try {
    let data = {
      trays: [
        {
          findTrayId: true,
          articleId: article.id,
          quantity: 1,
          unit: "pcs",
          name: tray.name, //Tray name, e.g. A1
          usage: tray.usage,
          userId: tray.userId,
        }
      ]
    };

    let result = await cabinetService.updateCabinetTrayStock(cabinetId.value, data);
  }
  catch (err) {
    return dialog(err);
  }
  */

  let tray = returnArticle.value;

  try {
    if (!tray || !tray.id) {
      return dialog(new Error(t('gloves.tray_data_missing')))
    }

    tray.userId = user.value?.id;
    tray.usage = "used";

    let changes = {
      usage: tray.usage,
      userId: tray.userId,
    };

    let result = await cabinetService.updateCabinetTray(cabinetId.value, tray.id, changes);
    if (result.count != 1) {
      return dialog(new Error(t('gloves.db_record_not_updated')))
    }
  }
  catch (err) {
    return dialog(err);
  }

  if (preventDuplicate.value != "return") {
    preventDuplicate.value = "return";
    try {
      let trayName = tray.name || tray.trayName;
      await inventoryService.returnInventoryItem({
        cabinetId: cabinetId.value,
        userId: user.value?.id,
        trayName: trayName,
        gloveSize: tray.gloveSize,
      });
    }
    catch (err) {
      console.error("[gloves] returnInventoryItem", err);
    }

    try {
      let trayName = tray.name || tray.trayName;
      let result = await logService.insertLog({
        uid: user.value?.displayName || user.value?.username,
        tid: trayName,
        msg: `Vračilo uporabljene rokavice ${tray.gloveSize} v predal "${trayName}".`,
        t: "gloves",
        ts: new Date()
      });
    }
    catch (err) {
      console.error(err);
    }
  }

  await refreshBorrowedTrays();
}

const preventDuplicate = ref(null);

async function cancelTakeArticle() {
  isTakingProcedure.value = isOpen.value;
  isChoosingAction.value = false;
}

async function confirmTakeArticle() {
  isTakingProcedure.value = isOpen.value;
  isChoosingAction.value = false;

  //TODO: Save to database

  /*
  if (!itemAvailable.value?.id) {
    return dialog(new Error("itemAvailable not found!"))
  }

  let article = rows.value.find(x => x.id == itemAvailable.value.id);
  if (!article) {
    return dialog(new Error("Article not found!"))
  }

  let tray = article.trays.find(x => x.name == takeArticleTrayName.value);
  if (!tray) {
    return dialog(new Error("Tray not found!"))
  }

  tray.userId = user.value?.id;
  tray.usage = "borrowed";

  try {
    let data = {
      trays: [
        {
          findTrayId: true,
          articleId: article.id,
          quantity: 1,
          unit: "pcs",
          name: tray.name, //Tray name, e.g. A1
          usage: tray.usage,
          userId: tray.userId
        }
      ]
    };

    let result = await cabinetService.updateCabinetTrayStock(cabinetId.value, data);
  }
  catch (err) {
    return dialog(err);
  }
  */

  let tray = takeArticle.value;

  try {
    if (!tray || !tray.id) {
      return dialog(new Error(t('gloves.tray_data_missing')))
    }

    tray.userId = user.value?.id;
    tray.usage = "borrowed";

    let changes = {
      usage: tray.usage,
      userId: tray.userId,
    };

    let trayName = tray.name || tray.trayName;
    console.log("[gloves] update cabinet tray", cabinetId.value, tray.id, trayName, tray.usage, tray.userId);

    let result = await cabinetService.updateCabinetTray(cabinetId.value, tray.id, changes);
    if (result.count != 1) {
      return dialog(new Error(t('gloves.db_record_not_updated')))
    }
  }
  catch (err) {
    return dialog(err);
  }

  if (preventDuplicate.value != "take") {
    preventDuplicate.value = "take";
    try {
      let trayName = tray.name || tray.trayName;
      let pickupData = {
        cabinetId: cabinetId.value,
        userId: user.value?.id,
        trayName: trayName,
        gloveSize: tray.gloveSize,
      };
      if (isEmptyTrayReplacement.value) {
        isEmptyTrayReplacement.value = false; //Used only once
        pickupData.replacement = true;
      }
      await inventoryService.pickupInventoryItem(pickupData);
    }
    catch (err) {
      console.error("[gloves] pickupInventoryItem", err);
    }

    try {
      let trayName = tray.name || tray.trayName;
      let result = await logService.insertLog({
        uid: user.value?.displayName || user.value?.username,
        tid: trayName,
        msg: `Izposoja rokavice ${tray.gloveSize} iz predala "${trayName}".`,
        t: "gloves",
        ts: new Date()
      });
    }
    catch (err) {
      console.error(err);
    }
  }

  await refreshBorrowedTrays();
}

const canOpenTray = computed(() => {
  if (!selected.value) {
    return false;
  }
  if (!selected.value.trays || selected.value.trays.length == 0) {
    return !!selected.value.tray; //Backward compatible
    //return false; //For updated versions
  }
  if (selected.value.trays.length > 0) {
    return selectedTray.value != null; //Must be selected
  }
  return true;
});

watch(isOpen, async (value, previous) => {
  //Borrow mode
  if (isReturnProcedure.value) {
    if (autoConfirm.value) {
      confirmReturnArticle();
    }

    //Tray just opened
    if (value && !previous) {
      isChoosingAction.value = true;
    }

    //Tray just closed
    if (!value && previous) {
      isReturnProcedure.value = isChoosingAction.value;

      if (temporaryGloveSize.value) {
        selectedGloveSize.value = temporaryGloveSize.value;
        temporaryGloveSize.value = null;
      }
    }
  }

  //Borrow mode
  if (isTakingProcedure.value) {
    if (autoConfirm.value) {
      confirmTakeArticle();
    }

    //Tray just opened
    if (value && !previous) {
      isChoosingAction.value = true;
    }

    //Tray just closed
    if (!value && previous) {
      isTakingProcedure.value = isChoosingAction.value;
    }
  }

  if (isModalVisible.value && !isEditStockQuantity.value && previous == true && value == false) {
    closeModal(); //Automatically close modal window when door is closed
  }

  /*
  if (!value && previous) {
    try {
      let result = await logService.insertLog({
        uid: user.value?.displayName || user.value?.username,
        tid: tray.name,
        msg: `Predal "${tray.name}" je zaprt.`,
        ts: new Date()
      });
    }
    catch (err) {
      console.error(err);
    }
  }
  */
});

const userGloveSize = ref(null);
// const userGloveSize = computed(() => {
//   return user.value?.preferences?.gloveSize; //S, M, L
// });

const techProblemText = ref("");

const isTechProblemVisible = ref(false);

function showTechProblemWindow() {
  isTechProblemVisible.value = true;
}

function hideTechProblemWindow() {
  isTechProblemVisible.value = false;
}

const isEmptyTray = computed({
  get() {
    return true;
  },
  set(value) {
    //No change
  }
});

const canReportError = ref(false);
const isEmptyTrayReplacement = ref(false);
const isEmptyTrayVisible = ref(false);

function showEmptyTrayWindow() {
  isEmptyTrayVisible.value = true;
}

function hideEmptyTrayWindow() {
  isEmptyTrayVisible.value = false;
}

const selectedGloveSize = ref(null);
const highlightedGloveSize = ref(null);
const isSelectSizeVisible = ref(false);

function highlightGloveSize(value) {
  highlightedGloveSize.value = value;
}

function applyGloveSize() {
  if (highlightedGloveSize.value) {
    selectedGloveSize.value = highlightedGloveSize.value;
  }
  loadGloveTrays();
  hideSelectSizeWindow();
}

function showSelectSizeWindow() {
  highlightedGloveSize.value = null;
  isSelectSizeVisible.value = true;
}

function hideSelectSizeWindow() {
  isSelectSizeVisible.value = false;
}

const selectedUser = ref(null);
const selectedUserName = ref("Uporabnik");
const highlightedUser = ref(null);
const isSelectUserVisible = ref(false);

function highlightUser(value) {
  highlightedUser.value = value;
}

async function applyUser() {
  try {
    if (highlightedUser.value) {
      selectedUser.value = highlightedUser.value;
    }
    await loadUser(selectedUser.value);

    //Apply selected user's profile on gloves
    await refreshBorrowedTrays();

    //Allow to choose other glove size if no user's size is available at the moment
    canSelectOtherSize.value = availableGlovesCount.value == 0;

    hideUserWindow();
  }
  catch (err) {
    hideUserWindow();
    dialog(err);
  }
}

function showUserWindow() {
  isSelectUserVisible.value = true;
}

function hideUserWindow() {
  isSelectUserVisible.value = false;
}

const allowSelectUser = ref(false);

const canSelectUser = computed(() => {
  if (!isReturnProcedure.value && !isTakingProcedure.value && !isOpening.value) {
    if (availableUsers.value && availableUsers.value.length > 0) {
      return allowSelectUser.value;
    }
  }
  return false;
});

const pendingAction = ref(false);
let pendingFunc = null;

async function sendEmptyTrayReport() {
  console.log("sendEmptyTrayReport")
  preventDuplicate.value = null;

  //Tray name of reported gloves is required
  let tray = borrowedTray.value;
  if (!tray) {
    hideEmptyTrayWindow();
    dialog(new Error(t('gloves.tray_data_missing')));
    return;
  }

  //In case tray is open - ask to close the tray
  if (isOpen.value) {
    pendingAction.value = true;
    pendingFunc = async () => {
      console.log("pending action")
      await sendEmptyTrayReport();
    };
    hideEmptyTrayWindow();
    return;
  }

  isEmptyTrayReplacement.value = true;
  console.log("isEmptyTrayReplacement", true)

  try {
    let trayName = tray.name || tray.trayName;
    console.log("returnInventoryItem missing", true, trayName);
    await inventoryService.returnInventoryItem({
      cabinetId: cabinetId.value,
      userId: user.value?.id,
      trayName: trayName,
      gloveSize: tray.gloveSize,
      missing: true
    });
  }
  catch (err) {
    console.error("[gloves] returnInventoryItem", err);
  }

  try {
    let trayName = tray.name || tray.trayName;
    console.log("logService.insertLog", trayName);
    let result = await logService.insertLog({
      uid: user.value?.displayName || user.value?.username,
      tid: trayName,
      msg: `Uporabnik javil napako: rokavice velikosti ${tray.gloveSize} ni v predalu "${trayName}".`,
      t: "gloves",
      ts: new Date()
    });
  }
  catch (err) {
    console.error(err);
  }

  hideEmptyTrayWindow();
  setTimeout(() => {
    takeNextArticle();
  //}, 300); //Works well, but sometimes still skips a tray
  }, 500);
}

function sendReport() {
  notImplemented();
}

async function notImplemented() {
  await dialog({
    type: "warning",
    title: t('gloves.under_development'),
    text: t('gloves.feature_not_available'),
    confirmButtonText: t('generic.ok'),
  });
}

//UI scaling factor in percent
const scale = ref(100); //100% by default, could also be 125% or 150%

const scaleButtonSize = computed(() => {
  if (scale.value == 125) {
    return "lg";
  }
  if (scale.value == 150) {
    return "xl";
  }
  return "md";
});

//setTimeout(() => {
  //hasBorrowedGlovesOtherCabinet.value = true;
//}, 1000)
</script>

<template>
  <div class="relative flex flex-col overflow-x-hidden" :class="{ 'ui-scale-100': scale == 100, 'ui-scale-125': scale == 125, 'ui-scale-150': scale == 150 }">

    <!-- Page content loading -->
    <div class="grid place-items-center w-full h-full overflow-x-hidden" v-if="isLoading && !warningDoorOpen">
      <Spinner size="12"></Spinner>
    </div>

    <!-- Header -->
    <div class="w-full mx-auto flex flex-shrink items-center justify-between" v-if="!isLoading">

      <!-- Title -->
      <!-- <div><span class="text-lg font-bold dark:text-white">Rokavice</span></div> -->
      <div></div>

      <!-- Buttons -->
      <!-- <div class="flex flex-row justify-items-strech space-x-2" v-if="canSelectUser">
        <div class="mt-2 mr-1">Izberi rokavice v imenu uporabnika</div>
        <Button type="secondary" @click="showUserWindow()" v-if="canSelectUser"><i class="fa fa-user mr-2"></i>{{ selectedUserName }}</Button>
      </div> -->
      <div class="grid impersonate-container" v-if="canSelectUser && !warningDoorOpen">
        <div class="impersonate-label">{{ $t('gloves.select_for_user') }}</div>
        <Button type="secondary" :size="scaleButtonSize" @click="showUserWindow()"><i class="fa fa-user mr-2"></i>{{ selectedUserName }}</Button>
      </div>

      <!-- Buttons -->
      <div class="flex flex-row justify-items-strech space-x-2">

        <!-- Report tech problem -->
        <!-- <Button type="secondary" @click="showTechProblemWindow()" v-if="isTakingProcedure && !isOpening"><i class="fa fa-bell"></i></Button> -->
        <Button type="secondary" :size="scaleButtonSize" @click="showEmptyTrayWindow()" v-if="isTakingProcedure && !isOpening && canReportError"><i class="fa fa-bell"></i></Button>
        <!-- <Button type="secondary" @click="showSelectSizeWindow()" v-if="selectedGloveSize && !hasBorrowedGloves && !isReturnProcedure && !isTakingProcedure && !isOpening && canSelectOtherSize"><i class="fa fa-hand mr-2"></i>{{ selectedGloveSize }}</Button> -->

      </div>
    </div>

    <!-- Door already open from before -->
    <div class="notice-container" v-if="warningDoorOpen">
      <p class="ui-scale-resize flex justify-center p-8" style="line-height: 2rem">
        Prosim zapri odprta vrata na omari.
      </p>
    </div>

    <!-- Already borrowed on the other cabinet -->
    <div class="notice-container" v-if="!isLoading && !warningDoorOpen && hasBorrowedGlovesOtherCabinet">
      <p class="ui-scale-resize flex justify-center p-8" style="line-height: 2rem">
        Rokavica je izposojena na drugi omari.
      </p>
    </div>

    <!-- Single article -->
    <div class="place-items-center flex flex-grow" v-if="!isLoading && !warningDoorOpen && !hasBorrowedGlovesOtherCabinet">

      <!-- Return article -->
      <div class="text-center mx-auto" v-if="hasBorrowedGloves">

        <!-- Glove size and icon -->
        <div class="ui-scale-resize">
          <div class="mb-4 font-bold" v-if="borrowedTray?.gloveSize">Rokavica {{ borrowedTray?.gloveSize }}</div>
          <!-- <pre class="bg-gray-100 border p-3">{{ borrowedTray }}</pre>
          <pre class="bg-gray-100 border p-3">{{ selectedGloveSize }}</pre> -->
          <div class="grid place-items-center text-gray-200 mx-auto" :class="{ 'mb-4': scale == 100, 'mb-8': scale == 125, 'mb-16': scale == 150 }">
            <div style="font-size: 48px;">
              <i class="fa fa-hand fa-regular"></i>
            </div>
          </div>
        </div>

        <!-- <div class="mb-4 font-bold">{{ borrowedTray.name }}</div> -->
        <!-- <div class="grid place-items-center text-gray-200 border mx-auto mb-4" style="width: 250px; height: 250px;">
          <img :src="itemBorrowed?.article?.mediumImageUrl" alt="" style="width: 250px; max-height: 250px;" v-if="itemBorrowed?.article?.mediumImageUrl" />
          <div style="font-size: 48px;" v-else>
            <i class="fa fa-image"></i>
          </div>
        </div> -->

        <div class="ui-scale-resize" v-if="!isTakingProcedure && !isReturnProcedure && !isOpening">
          <div class="mb-4">Odpri predal za vračilo rokavice</div>
          <Button type="warning" class="w-48 h-16" @click="startReturnArticleProcedure()">Odpri predal</Button>
        </div>

        <!-- <div class="flex justify-center border rounded-md p-8 bg-amber-100" v-if="isReturnProcedure && isOpening"> -->
        <div class="ui-scale-resize flex justify-center p-4" v-if="isReturnProcedure && isOpening">
          <Spinner size="6" class="mr-6"></Spinner> <span>Odpiram predal {{ returnArticleTrayName }}...</span>
        </div>

        <!-- <p class="flex justify-center p-8" style="line-height: 2rem" v-if="isReturnProcedure && isOpening">
          Prosim, počakaj...
        </p> -->

        <!-- <div class="flex justify-center border rounded-md p-8 bg-green-200 text-green-700" v-if="isReturnProcedure && !isOpening && isOpen"> -->
        <div class="ui-scale-resize flex justify-center p-4 text-green-700" v-if="isReturnProcedure && !isOpening && isOpen">
          <i class="fa fa-lock-open mr-6"></i> <span>Predal {{ returnArticleTrayName }} je odprt</span>
        </div>

        <!-- <div class="flex justify-center border rounded-md p-8 bg-gray-200 text-gray-700" v-if="isReturnProcedure && !isOpening && !isOpen"> -->
        <div class="ui-scale-resize flex justify-center p-4 text-gray-700" v-if="isReturnProcedure && !isOpening && !isOpen">
          <i class="fa fa-lock mr-6"></i> <span>Predal {{ returnArticleTrayName }} je zaprt</span>
        </div>

        <!-- <p class="ui-scale-resize flex justify-center p-8" style="line-height: 2rem" v-if="isReturnProcedure && !isOpening">
          Prosim, vrni artikel nazaj v predal, zapri predal in klikni gumb Potrdi.<br />
          V kolikor se je zgodila pomota, zapri predal in klikni gumb Prekliči.
        </p> -->


        <!-- <div class="flex justify-center border rounded-md p-8 bg-green-200 text-green-700" v-if="isTakingProcedure && !isOpening && isOpen">
          <i class="fa fa-lock-open mr-6"></i> <span>Predal {{ takeArticleTrayName }} je odprt</span>
        </div> -->
        <div class="ui-scale-resize flex justify-center p-4 text-green-700" v-if="isTakingProcedure && !isOpening && isOpen && !pendingAction">
          <i class="fa fa-lock-open mr-6"></i> <span>Predal {{ takeArticleTrayName }} je odprt</span>
        </div>

        <p class="ui-scale-resize flex justify-center p-4" style="line-height: 2rem" v-if="isTakingProcedure && !isOpening && !pendingAction">
          Prosim, vzemi rokavico in zapri predal za nadaljevanje.
        </p>

        <p class="ui-scale-resize flex justify-center p-4 text-center" style="line-height: 2rem" v-if="isTakingProcedure && !isOpening && pendingAction">
          Manjkajoča rokavica je zabeležena.<br />
          Prosim, zapri predal za nadaljevanje.
        </p>

        <!-- <p class="flex justify-center p-4" style="line-height: 2rem" v-if="isTakingProcedure && !isOpening">
          <CheckBox v-model="isEmptyTray" label="Predal je prazen" />
        </p> -->

        <!-- <div class="flex justify-center gap-4" v-if="!isTakingProcedure && isReturnProcedure && isChoosingAction">
          <Button type="secondary" class="w-48 h-16" @click="() => cancelReturnArticle()">Prekliči</Button>
          <Button type="success" class="w-48 h-16" @click="() => confirmReturnArticle()">Potrdi</Button>
        </div> -->

        <div class="vertical-spacer h-32">
          <!-- To move up and balance visually -->
        </div>

      </div>

      <!-- Take article -->
      <div class="text-center mx-auto" v-else>

        <!-- Glove size and icon -->
        <div class="ui-scale-resize">
          <!-- <div class="mb-4 font-bold" v-if="isReturnProcedure && !isOpening && borrowedTray?.gloveSize">Rokavica {{ borrowedTray?.gloveSize }}</div> -->
          <div class="mb-4 font-bold" v-if="selectedGloveSize">Rokavica {{ selectedGloveSize }}</div>
          <div class="grid place-items-center text-gray-200 mx-auto mb-16">
            <div style="font-size: 48px;">
              <i class="fa fa-hand fa-regular"></i>
            </div>
          </div>
        </div>

        <div class="ui-scale-resize" v-if="!isReturnProcedure && !isTakingProcedure && !isOpening">
          <div v-if="quotaReached">
            <div class="mb-4">Število maksimalno dovoljenih rokavic je doseženo.</div>
          </div>
          <div v-else-if="selectedGloveSize">
            <div class="mb-4" v-if="availableGlovesCount > 0">Na voljo {{ availableGlovesCount }} kos{{ availableGlovesCount > 2 ? "ov" : (availableGlovesCount > 1 ? "a" : "") }}</div>
            <!-- <div class="mb-4" v-else>Na voljo ni nobenih kosov</div> -->
            <!-- <Button type="primary" class="w-48 h-16" :enabled="availableGlovesCount > 0" @click="takeNextArticle()">Odpri predal</Button> -->
            <div class="mb-4" v-if="!(availableGlovesCount > 0)">Za trenutno velikost ni več rokavic na voljo.</div>
            <div class="" v-if="availableGlovesCount > 0"><Button type="primary" class="w-48 h-16" :enabled="availableGlovesCount > 0" @click="takeNextArticle()">Odpri predal</Button></div>
            <div class="" v-if="!(availableGlovesCount > 0)"><Button type="purple" class="w-64 h-16" @click="showSelectSizeWindow()" v-if="selectedGloveSize && !hasBorrowedGloves && !isReturnProcedure && !isTakingProcedure && !isOpening && canSelectOtherSize">Izberi nadomestno velikost</Button></div>
          </div>
          <div v-else>
            <div class="mb-4">Uporabnik nima nastavljene velikosti rokavice.</div>
          </div>
        </div>

        <!-- <div class="flex justify-center border rounded-md p-8 bg-amber-100" v-if="isTakingProcedure && isOpening"> -->
        <div class="ui-scale-resize flex justify-center p-4" v-if="isTakingProcedure && isOpening">
          <Spinner size="6" class="mr-6"></Spinner> <span>Odpiram predal {{ takeArticleTrayName }}...</span>
        </div>

        <!-- <p class="flex justify-center p-8" style="line-height: 2rem" v-if="isTakingProcedure && isOpening">
          Prosim, počakaj...
        </p> -->

        <!-- <div class="flex justify-center border rounded-md p-8 bg-green-200 text-green-700" v-if="!isReturnProcedure && isTakingProcedure && !isOpening && isOpen"> -->
        <div class="ui-scale-resize flex justify-center p-4 text-green-700" v-if="!isReturnProcedure && isTakingProcedure && !isOpening && isOpen">
          <i class="fa fa-lock-open mr-6"></i> <span>Predal {{ takeArticleTrayName }} je odprt</span>
        </div>

        <!-- <div class="flex justify-center border rounded-md p-8 bg-gray-200 text-gray-700" v-if="!isReturnProcedure && isTakingProcedure && !isOpening && !isOpen"> -->
        <div class="ui-scale-resize flex justify-center p-4 text-gray-700" v-if="!isReturnProcedure && isTakingProcedure && !isOpening && !isOpen">
          <i class="fa fa-lock mr-6"></i> <span>Predal {{ takeArticleTrayName }} je zaprt</span>
        </div>

        <!-- <p class="flex justify-center p-8" style="line-height: 2rem" v-if="!isReturnProcedure && isTakingProcedure && !isOpening">
          Prosim, vzemi artikel iz predala, zapri predal in klikni gumb Potrdi.<br />
          V kolikor se je zgodila pomota, zapri predal in klikni gumb Prekliči.
        </p> -->



        <!-- <div class="flex justify-center border rounded-md p-8 bg-green-200 text-green-700" v-if="isReturnProcedure && !isOpening && isOpen">
          <i class="fa fa-lock-open mr-6"></i> <span>Predal {{ returnArticleTrayName }} je odprt</span>
        </div> -->
        <div class="ui-scale-resize flex justify-center p-4 text-green-700" v-if="isReturnProcedure && !isOpening && isOpen">
          <i class="fa fa-lock-open mr-6"></i> <span>Predal {{ returnArticleTrayName }} je odprt</span>
        </div>

        <p class="ui-scale-resize flex justify-center p-4" style="line-height: 2rem" v-if="isReturnProcedure && !isOpening">
          Prosim, vrni rokavico in zapri predal za nadaljevanje.<br />
        </p>



        <!-- <div class="flex justify-center gap-4" v-if="!isReturnProcedure && isTakingProcedure && isChoosingAction">
          <Button type="secondary" class="w-48 h-16" @click="() => cancelTakeArticle()">Prekliči</Button>
          <Button type="success" class="w-48 h-16" @click="() => confirmTakeArticle()">Potrdi</Button>
        </div> -->

        <!-- <div class="flex justify-center gap-4 mt-8" v-if="!isReturnProcedure && isTakingProcedure && isChoosingAction">
          <Button type="secondary" class="w-48 h-16" @click="() => confirmTakeArticle()"><i class="fa fa-exclamation-triangle text-amber-500 mr-2"></i> <span class="text-amber-500">Reklamacija</span></Button>
        </div> -->

        <div class="vertical-spacer h-32">
          <!-- To move up and balance visually -->
        </div>

      </div>

    </div>



    <!-- persistent not-escapable -->
    <Modal size="3xl" v-if="isModalVisible && selected" @close="closeModalConditional"  @click.outside="closeModalConditional">
      <template #header>
        <div class="flex items-center text-lg">
          Artikel {{ selected.sku }}
        </div>
      </template>
      <template #body>

        <!-- Related articles -->
        <div class="w-full" v-if="isRelatedArticlesView && selected.related" style="min-height: 250px; max-height: 1000px;">
          <h3 class="font-bold">Sorodni artikli</h3>
          <div class="relative grid grid-cols-1 gap-4 w-full mt-2 mb-2">
            <a href="#" v-for="(row, i) in mapRelated(selected.related)" @click.prevent="showModal(row)" class="py-4 flex bg-white border border-gray-200 rounded-lg shadow hover:border-blue-600 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">

              <!-- Article image -->
              <div class="grid place-items-center text-center w-24 mx-4" style="width: 50px; height: 50px" v-if="row.thumbnailUrl">
                <img :src="row.thumbnailUrl" alt="" style="max-width: 50px; max-height: 50px;" />
              </div>
              <div class="grid place-items-center text-gray-200 text-center w-24 mx-4" :class="{ 'text-blue-200': selectedItemId == row.id && promptDeleteItemId != row.id, 'text-red-300': promptDeleteItemId == row.id }" style="font-size: 48px; width: 50px; height: 50px;" v-else>
                <i class="fa fa-image"></i>
              </div>

              <!-- Article name and SKU -->
              <div class="w-full pr-3 grid items-center">
                <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.name">{{ row.name }}</h5>
                <h3 class="mt-0 text-slate-400" v-if="row.sku">{{ row.sku }}</h3>
              </div>

            </a>
          </div>
        </div>

        <div class="flex flex-row w-full gap-8 h-72 mb-4" style="min-height: 250px; max-height: 1000px;" v-else>

          <!-- Article image -->
          <div class="grid place-items-center text-gray-200 border" style="width: 220px; height: 250px;" v-if="!isRelatedArticlesView">
            <img :src="selected.mediumImageUrl" alt="" style="width: 250px; max-height: 250px;" v-if="selected.mediumImageUrl" />
            <div style="font-size: 48px;" v-else>
              <i class="fa fa-image"></i>
            </div>
          </div>

          <!-- Article title, description, specifications -->
          <div class="flex flex-col grow gap-4 relative" v-if="!isRelatedArticlesView">
            <p class="font-bold">{{ selected.name }}</p>
            <p class="" v-if="selected.description">{{ selected.description }}</p>
            <p class="" v-if="hasParameterSelected">
              <h4 class="font-semibold">Specifikacija</h4>
              <div v-for="spec in parametersSelected">{{ spec.name }}: {{ spec.value }}</div>
            </p>
            <p v-if="!isCabinetBorrowMode">Na zalogi <span class="font-medium">{{ stockSum(selected.trays) }}</span> {{ selected.stockUnit || "kos" }}</p>
            <!-- <p v-if="selected.tray">Predal: <span class="font-medium">{{ selected.tray }}</span></p> -->
            <p v-if="selected.trays && selected.trays.length && false">
              <p class="mb-2">Izbran predal za odpiranje</p>
              <div class="grid grid-cols-4 gap-2">
                <!-- <Button :type="selectedTray == tray.name ? 'primary' : 'secondary'" :enabled="!isEditStockQuantity" v-for="tray of selected.trays" @click="selectTray(tray)">
                  <span class="font-medium">{{ tray.name }}</span><span class="" v-if="tray.quantity && !isCabinetBorrowMode">: {{ tray.quantity }} kos</span>
                  <span class="ml-2" v-else-if="isCabinetBorrowMode && tray.usage == 'new'"><i class="fa fa-check text-green-500"></i></span>
                  <span class="ml-2" v-else-if="isCabinetBorrowMode && tray.usage == 'used'"><i class="fa fa-xmark text-red-500"></i></span>
                  <span class="ml-2" v-else-if="isCabinetBorrowMode && tray.usage == 'borrowed'"><i class="fa fa-arrow-up-from-bracket text-amber-500"></i></span>
                  <span class="ml-2" v-else-if="isCabinetBorrowMode && tray.usage == 'empty'"><i class="fa fa-square fa-regular text-gray-400"></i></span>
                </Button> -->
                <DropDown :placeholder="$t('gloves.select_tray')" :options="availableTrayOptions" v-model="availableTraySelect" :enabled="!isEditStockQuantity && availableTrayOptions.length > 1" class="w-48" @change="selectTray()" />
                <!-- <DropDown placeholder="Izberi predal" :options="availableTrayOptions" v-model="availableTraySelect" :enabled="!isEditStockQuantity" class="w-64" @change="selectTray()" v-if="availableTrayOptions.length > 1" />
                <Button :type="selectedTray == tray.name ? 'primary' : 'secondary'" :enabled="!isEditStockQuantity" v-for="tray of availableTrayOptions" @click="selectTray(tray)" v-if="availableTrayOptions.length == 1">
                  <span class="font-medium">{{ tray.name }}</span><span class="" v-if="tray.quantity && !isCabinetBorrowMode">: {{ tray.quantity }} kos</span>
                </Button> -->
              </div>
            </p>

            <!-- Edit mode -->
            <div v-if="isEditStockQuantity && !isCabinetBorrowMode">
              <div>{{ $t('catalog.edit_stock') }}</div>
              <div class="flex flex-row mt-2">
                <Button type="secondary" @click="updateQuantity(-10)"><i class="fa fa-arrow-down"></i>&nbsp;-10</Button>
                <button class="ml-2 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" style="border-radius: 7px 0 0 7px;" @click="updateQuantity(-1)"><i class="fa fa-arrow-down"></i></button>
                <input type="text" :placeholder="$t('gloves.quantity_placeholder')" v-model="virtualStockQuantity" class="w-36 bg-white border border-gray-300 text-gray-900 text-sm block p-2.5 focus:ring-blue-500 focus:border-blue-500" />
                <button class="mr-2 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" style="border-radius: 0 7px 7px 0;" @click="updateQuantity(+1)"><i class="fa fa-arrow-up"></i></button>
                <Button type="secondary" @click="updateQuantity(+10)"><i class="fa fa-arrow-up"></i>&nbsp;+10</Button>
              </div>
            </div>

            <!-- <p>Urejanje zaloge</p>
            <div class="flex flex-row gap-2">
              <DropDown placeholder="Predal" :options="trayList" v-model="traySelect" class="w-32" />
              <TextBox placeholder="Količina" v-model="quantity" class="w-32" />
              <DropDown placeholder="kos" :options="unitList" v-model="unitSelect" class="w-32" />
            </div> -->

            <!--<div class="absolute flex flex-row gap-4 justify-items-stretch" style="bottom: 0">
              <button @click="openAttachment" type="button" class="bg-white text-blue-700 border rounded-lg hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
                <i class="fa fa-paperclip mr-2"></i> Specifikacija
              </button>
              <button @click="openAttachment" type="button" class="bg-white text-blue-700 border rounded-lg hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
                <i class="fa fa-paperclip mr-2"></i> Navodila
              </button>
            </div>-->

            <div v-if="isEditStockQuantity && isCabinetBorrowMode">
              <div>Stanje artikla v predalu</div>
              <div class="grid grid-cols-4 gap-2 mt-2" v-if="isEditStockQuantity && isCabinetBorrowMode">
                <Button :type="selectedUsage == 'borrowed' ? 'primary' : 'secondary'" @click="setArticleUsage('borrowed')"><i class="fa fa-arrow-right-from-bracket mr-2"></i>Vzel</Button>
                <Button :type="selectedUsage == 'used' ? 'primary' : 'secondary'" @click="setArticleUsage('used')"><i class="fa fa-arrow-right-to-bracket mr-2"></i>Vrnil</Button>
              </div>
            </div>

          </div>

          

        </div>
      </template>
      <template #footer>
        <div class="flex justify-between">
          <Button type="secondary" @click="showRelatedArticles()" v-if="!isEditStockQuantity && !isOpening && !isOpen && !isRelatedArticlesView && hasRelatedArticles">Sorodni&nbsp;artikli</Button>
          <Button type="secondary" @click="hideRelatedArticles()" v-if="!isEditStockQuantity && !isOpening && !isOpen && isRelatedArticlesView"><i class="fa fa-arrow-left inline mr-2"></i>Nazaj</Button>
          <div class="flex justify-items-end w-full" style="min-height: 30px;">
            <div class="m-auto">
              <span v-if="!isOpen && isOpening" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600">
                <Spinner size="6" style="display: inline-block;" /> <span style="display: inline-block; position: relative; top: 3px; left: 10px;">Odpiram predal {{ selectedTray }}</span>
              </span>
              <span v-if="isOpen && !isPrevOpen && !isEditStockQuantity" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600">
                <i class="fa fa-lock-open text-green-500" style="position: relative; top: 2px;"></i> <span style="display: inline-block; position: relative; top: 3px; left: 10px;">{{ $t('catalog.tray_open_message') }}</span>
              </span>
              <span v-if="isPrevOpen && !isOpening" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600">
                <i class="fa fa-triangle-exclamation text-amber-500" style="position: relative; top: 3px;"></i> <span style="display: inline-block; position: relative; top: 3px; left: 10px;">Za nadaljevanje najprej zapri prejšnji predal.</span>
              </span>
            </div>
            <div v-if="!isEditStockQuantity && !isRelatedArticlesView && !isOpening && !isOpen && selected.trays && selected.trays.length" class="mr-2">
              <DropDown :placeholder="$t('gloves.select_tray')" :options="availableTrayOptions" v-model="availableTraySelect" :enabled="!isEditStockQuantity && availableTrayOptions.length > 1" class="w-48" @change="selectTray()" />
            </div>
            <Button type="primary" :enabled="canOpenTray" @click="openTray(selectedTray)" v-if="!isEditStockQuantity && !isRelatedArticlesView && !isOpening && !isOpen && selected.tray">{{ $t('gloves.open_tray') }}</Button>
            <Button type="secondary" @click="closeModal()" v-if="!isEditStockQuantity && !isOpening && !isOpen && !selected.tray">{{ $t('generic.close') }}</Button>
            <Button type="primary" @click="applyStock(selected)" v-if="isEditStockQuantity">{{ $t('generic.confirm') }}</Button>
          </div>
        </div>
      </template>
    </Modal>



    <Modal size="3xl" v-if="isTechProblemVisible" @close="hideTechProblemWindow">
      <template #header>
        <div class="flex items-center text-lg">
          {{ $t('gloves.error_label') }}
        </div>
      </template>
      <template #body>
        <div>
          <div>
            <Label :required="true">{{ $t('gloves.description_label') }}</Label>
            <TextArea v-model="techProblemText" :placeholder="$t('gloves.message_placeholder')" :rows="7" class="w-full" />
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end">
          <Button type="primary" @click="sendReport()">Pošlji</Button>
        </div>
      </template>
    </Modal>


    <Modal size="3xl" v-if="isEmptyTrayVisible" @close="hideEmptyTrayWindow">
      <template #header>
        <div class="flex items-center text-lg">
          {{ $t('gloves.error_label') }}
        </div>
      </template>
      <template #body>
        <div class="w-full">
          <div class="w-full">
            <Label :required="true">Opis napake</Label>
            <div class="w-full flex gap-2">
              <i class="fa fa-xmark text-red-500 relative top-0.5"></i> <span>Predal {{ takeArticleTrayName }} je prazen</span>
              <!-- <CheckBox v-model="isEmptyTray" :label="'Predal ' + takeArticleTrayName + ' je prazen'" :enabled="true" :readonly="true" /> -->
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end">
          <Button type="primary" @click="sendEmptyTrayReport()">Pošlji in odpri naslednji predal</Button>
        </div>
      </template>
    </Modal>

    <!-- Select glove size -->
    <Modal size="3xl" v-if="isSelectSizeVisible" @close="hideSelectSizeWindow">
      <template #header>
        <div class="flex items-center text-lg">
          {{ $t('gloves.select_replacement') }}
        </div>
      </template>
      <template #body>
        <div class="w-full">
          <div class="w-full">
            <Label :required="true">Izberi nadomestno velikost in potrdi</Label>
            <div class="w-full flex gap-2">
              
              <div class="flex flex-row gap-2 mt-2 mb-2">
                <a href="#" v-for="(item, i) in gloveSizeOptions" :key="'dl' + i" @click.prevent="highlightGloveSize(item.value)" class="glove-size-button" :class="{ 'glove-size-highlighted': highlightedGloveSize == item.value, 'glove-size-selected': selectedGloveSize == item.value }">
                  <h5 class="glove-size-button-label">{{ item.value }}</h5>
                </a>
              </div>

            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end">
          <Button type="secondary" @click="hideSelectSizeWindow()">{{ $t('generic.cancel') }}</Button>
          <Button type="primary" class="ml-2" @click="applyGloveSize()">{{ $t('generic.confirm') }}</Button>
        </div>
      </template>
    </Modal>

    <!-- Select user -->
    <Modal size="3xl" v-if="isSelectUserVisible" @close="hideUserWindow">
      <template #header>
        <div class="flex items-center text-lg">
          Dodeli rokavice drugemu uporabniku
        </div>
      </template>
      <template #body>
        <div class="w-full">
          <div class="w-full">
            <Label :required="true">Izberi uporabnika</Label>
            <div class="w-full flex gap-2" style="max-height: 700px; overflow-y: scroll;" v-if="availableUsers && availableUsers.length">
              
              <!-- <div class="w-full grid grid-cols-3 gap-2 mt-2 mb-2">
                <a href="#" v-for="(item, i) in availableUsers" :key="'dl' + i" @click.prevent="highlightUser(item.id)" class="w-full select-user-button" :class="{ 'select-user-highlighted': highlightedUser == item.id, 'select-user-selected': selectedUser == item.id }">
                  <h5 class="select-user-button-label">{{ item.displayName || item.username || item.email || "Uporabnik" }}</h5>
                </a>
              </div> -->

              <div class="w-full grid gap-2 mt-2 mb-2">
                <a href="#" v-for="(item, i) in availableUsers" :key="'dl' + i" @click.prevent="highlightUser(item.id)" class="w-full select-user-button" :class="{ 'select-user-highlighted': highlightedUser == item.id, 'select-user-selected': selectedUser == item.id }">
                  <!-- <i class="fa fa-check text-green-500 pt-1.5 mr-4 ml-2"></i> -->
                  <h5 class="select-user-button-label mr-4">{{ item.displayName || item.username || item.email || "Uporabnik" }}</h5>
                </a>
              </div>

            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end">
          <Button type="secondary" @click="hideUserWindow()">{{ $t('generic.cancel') }}</Button>
          <Button type="primary" class="ml-2" @click="applyUser()">{{ $t('generic.confirm') }}</Button>
        </div>
      </template>
    </Modal>

  </div>
</template>

<style scoped>
.flip-x {
  -moz-transform: scale(-1, 1);
  -webkit-transform: scale(-1, 1);
  -o-transform: scale(-1, 1);
  -ms-transform: scale(-1, 1);
  transform: scale(-1, 1);
}

.parameter-option {
  @apply px-2 py-3 border border-gray-200 rounded-lg shadow bg-white hover:bg-gray-100 outline-none; /* dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 */
}

.parameter-option-compact {
  @apply px-3 py-2 border border-gray-200 text-sm rounded-lg shadow bg-white hover:bg-gray-100 outline-none;
}

.parameter-option-compact-locked {
  @apply px-3 py-2 border border-gray-100 text-sm rounded-lg shadow bg-gray-50 outline-none;
}

.parameter-option.option-selected,
.parameter-option-compact.option-selected {
  @apply border-blue-400 bg-blue-300 hover:bg-blue-200;
}

.glove-size-button {
  @apply w-16;
  @apply flex justify-center aspect-square p-4 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
}

.glove-size-button-label {
  @apply pt-0 text-xl;
  @apply font-bold tracking-tight dark:text-white;
}

/*
@media only screen and (max-width: 1024px) { 
  .glove-size-button {
    @apply w-14 p-3;
  }

  .glove-size-button-label {
    @apply text-lg;
  }
}
*/

.glove-size-button.glove-size-highlighted {
  border: 2px solid rgba(26,86,219,1);
}

.glove-size-button.glove-size-selected,
.glove-size-button.glove-size-selected:hover {
  @apply bg-blue-700 text-white;
  color: white !important;
  cursor: default;
}






.select-user-button {
  @apply flex justify-center p-4 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
}

.select-user-button-label {
  @apply pt-0 text-xl;
  @apply tracking-tight dark:text-white;
}

.select-user-button.select-user-highlighted {
  border: 2px solid rgba(26,86,219,1);
}

.select-user-button.select-user-selected,
.select-user-button.select-user-selected:hover {
  @apply bg-blue-700 text-white;
  color: white !important;
  cursor: default;
}



.notice-container {
  @apply flex justify-center items-center flex-grow w-full; 
}

.ui-scale-100 {
  .impersonate-container {
    @apply mt-4;
  }

  .impersonate-label {
    @apply mb-2;
  }

  .notice-container p {
    @apply scale-100; 
  }
}

.ui-scale-125 {
  @apply overflow-hidden;

  .impersonate-container {
    @apply mt-4;
  }

  .impersonate-label {
    @apply text-lg mb-2;
  }

  .ui-scale-resize {
    @apply scale-125;
  }
}

.ui-scale-150 {
  .impersonate-container {
    @apply mt-8;
  }

  .impersonate-label {
    @apply text-xl mb-2;
  }

  .ui-scale-resize {
    @apply scale-150;
  }
}

</style>
